import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { format, subDays, addDays, isToday, parseISO } from 'date-fns';
import { fi } from 'date-fns/locale';
import {
  ChevronLeft,
  ChevronRight,
  Footprints,
  Moon,
  Heart,
  Flame,
  Activity,
  Settings,
  TrendingUp,
  Link as LinkIcon,
  RefreshCw,
  X,
  Loader2,
} from 'lucide-react';
import { healthApi, wearableApi } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import type { DailyHealthSummary, WearableActivity, WorkoutLinkSuggestion } from '@/types';

function formatMinutesToHours(minutes: number | null): string {
  if (minutes === null) return '-';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}min`;
}

function HealthCard({
  icon: Icon,
  title,
  value,
  subtitle,
  color,
}: {
  icon: React.ElementType;
  title: string;
  value: string | number;
  subtitle?: string;
  color: string;
}) {
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function SleepBreakdown({ data }: { data: DailyHealthSummary }) {
  const total = data.sleepDurationMinutes || 0;
  const deep = data.deepSleepMinutes || 0;
  const light = data.lightSleepMinutes || 0;
  const rem = data.remSleepMinutes || 0;
  const awake = data.awakeDurationMinutes || 0;

  if (total === 0) return null;

  const segments = [
    { value: deep, color: 'bg-indigo-600', label: 'Syvä' },
    { value: light, color: 'bg-sky-400', label: 'Kevyt' },
    { value: rem, color: 'bg-purple-500', label: 'REM' },
    { value: awake, color: 'bg-gray-300 dark:bg-gray-600', label: 'Hereillä' },
  ];

  return (
    <div className="mt-3">
      <div className="flex h-2 rounded-full overflow-hidden">
        {segments.map((seg, i) => (
          <div
            key={i}
            className={seg.color}
            style={{ width: `${(seg.value / total) * 100}%` }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
        {segments.map((seg, i) => (
          seg.value > 0 && (
            <div key={i} className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${seg.color}`} />
              <span>{seg.label}: {formatMinutesToHours(seg.value)}</span>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

function LinkActivityModal({
  activity,
  onClose,
  onLinked,
}: {
  activity: WearableActivity;
  onClose: () => void;
  onLinked: () => void;
}) {
  const { data: suggestions, isLoading } = useQuery({
    queryKey: ['link-suggestions', activity.id],
    queryFn: () => healthApi.getLinkSuggestions(activity.id),
  });

  const linkMutation = useMutation({
    mutationFn: (workoutId: string) => healthApi.linkActivity(activity.id, workoutId),
    onSuccess: () => {
      onLinked();
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Linkitä treeniin
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Valitse treeni johon linkitetään:{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {activity.activityName || activity.activityType}
            </span>
          </p>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : suggestions && suggestions.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {suggestions.map((workout) => (
                <button
                  key={workout.id}
                  onClick={() => linkMutation.mutate(workout.id)}
                  disabled={linkMutation.isPending}
                  className="w-full text-left p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  <p className="font-medium text-gray-900 dark:text-white">
                    {workout.name || 'Nimetön treeni'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {format(new Date(workout.startedAt), 'd.M.yyyy HH:mm', { locale: fi })}
                  </p>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              Ei treenejä samalle päivälle
            </p>
          )}

          {linkMutation.isPending && (
            <div className="flex items-center justify-center gap-2 mt-4 text-primary-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Linkitetään...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityItem({
  activity,
  onLink,
}: {
  activity: WearableActivity;
  onLink?: () => void;
}) {
  const startTime = new Date(activity.startTime);

  // Format pace for walking/running activities
  const formatPace = () => {
    if (activity.distance && activity.durationMinutes && Number(activity.distance) > 0) {
      const paceSecondsPerKm = (activity.durationMinutes * 60) / (Number(activity.distance) / 1000);
      const paceMinutes = Math.floor(paceSecondsPerKm / 60);
      const paceSeconds = Math.round(paceSecondsPerKm % 60);
      return `${paceMinutes}:${paceSeconds.toString().padStart(2, '0')} min/km`;
    }
    return null;
  };

  const pace = formatPace();
  const distanceKm = activity.distance ? (Number(activity.distance) / 1000).toFixed(2) : null;

  // Determine activity category
  const isCardioActivity = ['walking', 'running', 'cycling', 'hiking', 'swimming', 'cardio'].includes(
    activity.activityType?.toLowerCase() || ''
  );
  const isStrengthActivity = ['strength_training', 'weight_training', 'gym', 'strength'].includes(
    activity.activityType?.toLowerCase() || ''
  );

  return (
    <div className="py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Activity className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {activity.activityName || activity.activityType}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {format(startTime, 'HH:mm')} • {activity.durationMinutes} min
              {activity.calories && ` • ${activity.calories} kcal`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activity.linkedWorkout ? (
            <Link
              to={`/workouts/${activity.linkedWorkout.id}`}
              className="badge badge-success text-xs flex items-center gap-1"
            >
              <LinkIcon className="h-3 w-3" />
              Linkitetty
            </Link>
          ) : (
            <button
              onClick={onLink}
              className="btn btn-ghost text-xs text-primary-600 hover:text-primary-700"
            >
              Linkitä treeniin
            </button>
          )}
        </div>
      </div>

      {/* Heart rate stats row */}
      {(activity.avgHeartRate || activity.maxHeartRate || activity.minHeartRate) && (
        <div className="mt-2 ml-11 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <Heart className="h-3 w-3 text-red-500" />
            Syke:
          </span>
          {activity.avgHeartRate && (
            <span>{activity.avgHeartRate} bpm (ka)</span>
          )}
          {activity.minHeartRate && (
            <span>min {activity.minHeartRate}</span>
          )}
          {activity.maxHeartRate && (
            <span>max {activity.maxHeartRate}</span>
          )}
        </div>
      )}

      {/* Distance and pace row for cardio activities */}
      {isCardioActivity && (distanceKm || pace) && (
        <div className="mt-1 ml-11 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
          {distanceKm && (
            <span className="font-medium">{distanceKm} km</span>
          )}
          {pace && (
            <span>Vauhti: {pace}</span>
          )}
          {activity.elevationGain && activity.elevationGain > 0 && (
            <span>Nousu: {activity.elevationGain} m</span>
          )}
        </div>
      )}

      {/* Training effect row for strength activities */}
      {(activity.aerobicTE || activity.anaerobicTE) && (
        <div className="mt-1 ml-11 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
          {activity.aerobicTE && (
            <span>Aerobinen TE: {Number(activity.aerobicTE).toFixed(1)}</span>
          )}
          {activity.anaerobicTE && (
            <span>Anaerobinen TE: {Number(activity.anaerobicTE).toFixed(1)}</span>
          )}
        </div>
      )}

      {/* Intensity minutes row */}
      {(activity.moderateMinutes || activity.vigorousMinutes) && (
        <div className="mt-1 ml-11 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
          {activity.moderateMinutes && activity.moderateMinutes > 0 && (
            <span>Kohtuullinen: {activity.moderateMinutes} min</span>
          )}
          {activity.vigorousMinutes && activity.vigorousMinutes > 0 && (
            <span>Raskas: {activity.vigorousMinutes} min</span>
          )}
        </div>
      )}
    </div>
  );
}

export function HealthPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [linkingActivity, setLinkingActivity] = useState<WearableActivity | null>(null);
  const queryClient = useQueryClient();

  // Get date from URL or default to today
  const dateParam = searchParams.get('date');
  const selectedDate = dateParam ? parseISO(dateParam) : new Date();
  const dateString = format(selectedDate, 'yyyy-MM-dd');

  const setSelectedDate = (date: Date) => {
    const newDateString = format(date, 'yyyy-MM-dd');
    if (isToday(date)) {
      // Remove date param for today (cleaner URL)
      searchParams.delete('date');
    } else {
      searchParams.set('date', newDateString);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Fetch connection status
  const { data: connection } = useQuery({
    queryKey: ['wearable-connection'],
    queryFn: wearableApi.getConnection,
  });

  // Fetch daily health data
  const {
    data: healthData,
    isLoading: healthLoading,
    refetch: refetchHealth,
  } = useQuery({
    queryKey: ['health-daily', dateString],
    queryFn: () => healthApi.getDaily(dateString),
    enabled: !!connection,
  });

  // Fetch activities
  const { data: activities, isLoading: activitiesLoading, refetch: refetchActivities } = useQuery({
    queryKey: ['health-activities', dateString],
    queryFn: () => healthApi.getActivities(dateString),
    enabled: !!connection,
  });

  // Manual sync mutation
  const [syncing, setSyncing] = useState(false);
  const handleSync = async () => {
    setSyncing(true);
    try {
      await wearableApi.sync();
      refetchHealth();
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setSyncing(false);
    }
  };

  const goToPreviousDay = () => setSelectedDate(subDays(selectedDate, 1));
  const goToNextDay = () => setSelectedDate(addDays(selectedDate, 1));
  const goToToday = () => setSelectedDate(new Date());

  // If no connection, show setup prompt
  if (!connection) {
    return (
      <div className="text-center py-12">
        <Heart className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-4">
          Terveystiedot
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-md mx-auto">
          Yhdistä Garmin Connect -tilisi nähdäksesi terveystietosi ja aktiviteettisi.
        </p>
        <Link to="/health/settings" className="btn btn-primary mt-6">
          <Settings className="h-4 w-4 mr-2" />
          Yhdistä Garmin
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Terveys
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {connection.provider.displayName} - {connection.username}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/health/trends"
            className="btn btn-ghost text-gray-600 dark:text-gray-300"
          >
            <TrendingUp className="h-4 w-4 mr-1" />
            Trendit
          </Link>
          <Link
            to="/health/settings"
            className="btn btn-ghost text-gray-600 dark:text-gray-300"
          >
            <Settings className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Date selector */}
      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-3 shadow-xs">
        <button
          onClick={goToPreviousDay}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="text-center">
          <button
            onClick={goToToday}
            className="text-lg font-semibold text-gray-900 dark:text-white hover:text-primary-600"
          >
            {isToday(selectedDate)
              ? 'Tänään'
              : format(selectedDate, 'EEEE d.M.', { locale: fi })}
          </button>
          <p className="text-xs text-gray-500">
            {format(selectedDate, 'd. MMMM yyyy', { locale: fi })}
          </p>
        </div>
        <button
          onClick={goToNextDay}
          disabled={isToday(selectedDate)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Sync button for today */}
      {isToday(selectedDate) && (
        <button
          onClick={handleSync}
          disabled={syncing}
          className="w-full btn btn-ghost border border-gray-200 dark:border-gray-700 justify-center"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Synkronoidaan...' : 'Synkronoi tiedot'}
        </button>
      )}

      {/* Health cards */}
      {healthLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : healthData?.hasData ? (
        <div className="grid grid-cols-2 gap-4">
          <HealthCard
            icon={Footprints}
            title="Askeleet"
            value={healthData.steps?.toLocaleString('fi-FI') || '-'}
            subtitle={healthData.steps ? `${Math.round((healthData.steps / 10000) * 100)}% tavoitteesta` : undefined}
            color="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
          />
          <HealthCard
            icon={Flame}
            title="Kalorit"
            value={healthData.activeCalories || '-'}
            subtitle="aktiiviset kcal"
            color="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400"
          />
          <HealthCard
            icon={Heart}
            title="Leposyke"
            value={healthData.restingHeartRate || '-'}
            subtitle="bpm"
            color="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
          />
          <HealthCard
            icon={Moon}
            title="Uni"
            value={formatMinutesToHours(healthData.sleepDurationMinutes)}
            subtitle={healthData.sleepScore ? `Pisteet: ${healthData.sleepScore}` : undefined}
            color="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400"
          />
        </div>
      ) : (
        <div className="card p-6 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            Ei tietoja tälle päivälle
          </p>
        </div>
      )}

      {/* Sleep breakdown */}
      {healthData?.hasData && healthData.sleepDurationMinutes && (
        <div className="card p-4">
          <h3 className="font-medium text-gray-900 dark:text-white mb-2">
            Unen jakautuminen
          </h3>
          <SleepBreakdown data={healthData} />
        </div>
      )}

      {/* Activities */}
      <div className="card p-4">
        <h3 className="font-medium text-gray-900 dark:text-white mb-3">
          Päivän aktiviteetit
        </h3>
        {activitiesLoading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        ) : activities && activities.length > 0 ? (
          <div>
            {activities.map((activity) => (
              <ActivityItem
                key={activity.id}
                activity={activity}
                onLink={() => setLinkingActivity(activity)}
              />
            ))}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-center py-4">
            Ei aktiviteetteja
          </p>
        )}
      </div>

      {/* Link Activity Modal */}
      {linkingActivity && (
        <LinkActivityModal
          activity={linkingActivity}
          onClose={() => setLinkingActivity(null)}
          onLinked={() => {
            refetchActivities();
          }}
        />
      )}
    </div>
  );
}
